import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Switch } from '../../components/ui/switch';
import { Tag, Plus, Copy, XCircle } from 'lucide-react';

const COMMISSION_RATES = {
  affiliate: { silver: 0, gold: 10, platinum: 15 },  // silver = flat ₹300 (no % promo)
  referral: { silver: 10, gold: 15, platinum: 20 },
  solution: { silver: 15, gold: 20, platinum: 30 }
};

const PartnerPromoCodes = () => {
  const { user, getAuthHeaders, API_URL } = useAuth();
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount_percent: '',
    expiry_date: '',
    usage_limit: '',
    once_per_user: false,
  });

  const isFlatFeeAffiliate = user?.partner_type === 'affiliate' && user?.tier === 'silver';
  const maxDiscount = isFlatFeeAffiliate ? 0 : (COMMISSION_RATES[user?.partner_type || 'affiliate']?.[user?.tier || 'silver'] || 15);

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/partner/promo-codes`, {
        headers: getAuthHeaders()
      });
      setPromoCodes(response.data);
    } catch (error) {
      console.error('Failed to fetch promo codes:', error);
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        code: formData.code.trim().toUpperCase(),
        discount_percent: parseFloat(formData.discount_percent),
        expiry_date: formData.expiry_date,
        usage_limit: parseInt(formData.usage_limit),
        once_per_user: formData.once_per_user,
      };

      if (payload.discount_percent > maxDiscount) {
        toast.error(`Discount cannot exceed your commission rate (${maxDiscount}%)`);
        setSubmitting(false);
        return;
      }

      await axios.post(`${API_URL}/api/partner/promo-codes`, payload, {
        headers: getAuthHeaders()
      });

      toast.success('Promo code created successfully!');
      setFormData({ code: '', discount_percent: '', expiry_date: '', usage_limit: '', once_per_user: false });
      fetchPromoCodes();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to create promo code.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (codeId) => {
    try {
      await axios.put(`${API_URL}/api/partner/promo-codes/${codeId}/deactivate`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Promo code deactivated');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to deactivate promo code');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied "${code}" to clipboard`);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700 border-green-200',
      expired: 'bg-red-100 text-red-700 border-red-200',
      exhausted: 'bg-amber-100 text-amber-700 border-amber-200',
      deactivated: 'bg-gray-100 text-gray-500 border-gray-200',
      not_live: 'bg-amber-100 text-amber-800 border-amber-300'
    };
    return styles[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  // "Active" here used to mean "active in this database", which is not the question a partner is
  // asking. The question is whether a client can actually redeem it, and that is true only once
  // the platform has the code. Anything else is 'not_live', however healthy the portal record.
  const effectiveStatus = (promo) => {
    if (promo.status === 'active' && promo.sync_status !== 'ok') return 'not_live';
    return promo.status;
  };

  // Minimum expiry date is tomorrow
  const minExpiryDate = new Date(Date.now() + 86400000).toISOString().split('T')[0];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="partner-promo-codes">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">
            Promo Codes
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            Generate promo codes for new users. Max discount: <span className="font-semibold text-[#ef785b]">{maxDiscount}%</span> (based on your {user?.tier} tier)
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Create Form */}
          <Card className="bg-white border-[#E2E2DF]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Plus className="w-5 h-5 text-[#ef785b]" />
                Create Promo Code
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isFlatFeeAffiliate ? (
                <div className="text-center py-8 text-[#64748B]">
                  <Tag className="w-12 h-12 mx-auto mb-3 text-[#E2E2DF]" />
                  <p className="font-medium text-[#1A1A1A]">Promo codes unavailable</p>
                  <p className="text-sm mt-1">Silver affiliate partners earn a flat fee per client. Upgrade your tier to create percentage-based promo codes.</p>
                </div>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-[#1A1A1A]">Promo Code *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                    placeholder="e.g. SAVE20"
                    className="mt-1.5 h-10 border-[#E2E2DF] uppercase"
                    required
                    maxLength={20}
                    data-testid="promo-code-input"
                  />
                  <p className="text-xs text-[#64748B] mt-1">Letters, numbers only. Will be auto-uppercased.</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-[#1A1A1A]">Discount % *</Label>
                    <Input
                      type="number"
                      min="1"
                      max={maxDiscount}
                      step="1"
                      value={formData.discount_percent}
                      onChange={(e) => handleChange('discount_percent', e.target.value)}
                      placeholder={`Max ${maxDiscount}%`}
                      className="mt-1.5 h-10 border-[#E2E2DF]"
                      required
                      data-testid="promo-discount"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#1A1A1A]">Usage Limit *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.usage_limit}
                      onChange={(e) => handleChange('usage_limit', e.target.value)}
                      placeholder="e.g. 50"
                      className="mt-1.5 h-10 border-[#E2E2DF]"
                      required
                      data-testid="promo-usage-limit"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-[#FAFAF8] rounded-lg border border-[#E2E2DF]">
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A]">Once Per User</p>
                    <p className="text-xs text-[#64748B]">Each customer can use this code only once</p>
                  </div>
                  <Switch
                    checked={formData.once_per_user}
                    onCheckedChange={(v) => handleChange('once_per_user', v)}
                    data-testid="promo-once-per-user"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-[#1A1A1A]">Expiry Date *</Label>
                  <Input
                    type="date"
                    min={minExpiryDate}
                    value={formData.expiry_date}
                    onChange={(e) => handleChange('expiry_date', e.target.value)}
                    className="mt-1.5 h-10 border-[#E2E2DF]"
                    required
                    data-testid="promo-expiry"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 bg-[#ef785b] hover:bg-[#D44017] text-white"
                  data-testid="submit-promo"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      Creating...
                    </span>
                  ) : (
                    <>
                      <Tag className="w-4 h-4 mr-2" />
                      Generate Promo Code
                    </>
                  )}
                </Button>
              </form>
              )}
            </CardContent>
          </Card>

          {/* Promo Codes List */}
          <Card className="bg-white border-[#E2E2DF]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Tag className="w-5 h-5 text-[#64748B]" />
                Your Promo Codes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ef785b]"></div>
                </div>
              ) : promoCodes.length === 0 ? (
                <div className="text-center py-8 text-[#64748B]">
                  <Tag className="w-12 h-12 mx-auto mb-3 text-[#E2E2DF]" />
                  <p>No promo codes yet.</p>
                  <p className="text-sm">Create your first promo code using the form.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {promoCodes.map((promo, index) => (
                    <div key={promo.id} className="p-4 bg-[#FAFAF8] rounded-lg border border-[#E2E2DF]" data-testid={`promo-item-${index}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-mono font-bold text-[#1A1A1A]">{promo.code}</p>
                            <button
                              onClick={() => copyToClipboard(promo.code)}
                              className="text-[#64748B] hover:text-[#ef785b] transition-colors"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <p className="text-sm text-[#64748B]">{promo.discount_percent}% off</p>
                        </div>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadge(effectiveStatus(promo))}`}>
                          {effectiveStatus(promo) === 'not_live'
                            ? 'Not live yet'
                            : effectiveStatus(promo).charAt(0).toUpperCase() + effectiveStatus(promo).slice(1)}
                        </span>
                      </div>
                      {/* An active code that never reached checkout is the harm this page used to
                          hide: the partner gives it to a client and the client is told it is
                          invalid. Say so here rather than let them find out from the client. */}
                      {effectiveStatus(promo) === 'not_live' && (
                        <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                          This code is not yet live at checkout, so a client entering it will be
                          told it is invalid. It retries automatically — if it stays like this,
                          tell us.
                        </p>
                      )}
                      <div className="mt-2 flex items-center gap-4 text-xs text-[#64748B]">
                        <span>Used: {promo.usage_count}/{promo.usage_limit}</span>
                        <span>Expires: {new Date(promo.expiry_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        {promo.once_per_user && (
                          <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">1x per user</span>
                        )}
                      </div>
                      {promo.status === 'active' && (
                        <button
                          onClick={() => handleDeactivate(promo.id)}
                          className="mt-2 flex items-center gap-1 text-xs text-red-500 hover:text-red-700 transition-colors"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          Deactivate
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PartnerPromoCodes;
